import { Map as ImMap, Record } from 'immutable';
import type { Saga, SagaIterator } from 'redux-saga';
import { call, delay, put, race, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import {
  crashReporterInterface,
  getHttpStatus,
  getLogger,
  getQcsErrorCode,
  httpClientInterface,
  requireEnvironmentConfig,
  type TrackingProperties,
  tracker,
} from '../../utils/core';
import { accountsActions, accountsTransformers } from '../accounts';
import { actionHelpers, resourceSagaUtils, resourceStoreTypes } from '../core';
import type { Meta } from '../core/actionHelpers';
import { featureFlagsSelectors } from '../feature-flags';
import { institutionsActions, institutionsSelectors } from '../institutions';
import { type FiChannel, type Institution, mkFiIssue } from '../institutions/institutionsTypes';
import { rootUiActions, rootUiSelectors, rootUiTypes } from '../root-ui';
import * as actions from './institutionLoginsActions';
import {
  getActiveConnectedInstitutionLogins,
  getCredentialBlobsForRefreshingLogins,
  getCredentialsForRefreshingLogins,
  getIdsRefreshingWithUserActionsPending,
  getInstitutionLoginForId,
  getInstitutionLoginsById,
  getLastSyncDate,
  getTrackingAggregator,
  hasUserActionBeenQueued,
  isUserActionPending,
  wasUserActionCancelled,
} from './institutionLoginsSelectors';
import type { QCSInstitutionLogin } from './institutionLoginsTransformers';
import * as transformers from './institutionLoginsTransformers';
import {
  type InstitutionLogin,
  type InstitutionLoginProps,
  type InstitutionLoginStatus,
  type InstitutionLoginStore,
  mkInstitutionLogin,
  RefreshAccountsMfaFormSubmitError,
} from './institutionLoginsTypes';

const log = getLogger('data/institutionLogins/sagas');

const mkInstitutionLoginsPayload = resourceStoreTypes.createTypedQcsSyncResourcePayloadFactory<InstitutionLoginProps>();

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('transactionsDumpUpdater');
export function* institutionLoginsDumpUpdater(resources: QCSInstitutionLogin[]) {
  yield callFirst(institutionLoginsUpdate, resources);
  yield callLast(institutionLoginsNudgeUpdater);
}

function* institutionLoginsUpdate(resources: QCSInstitutionLogin[]): SagaIterator {
  const process: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpInstitutionLogins');
  if (process) {
    yield put(
      // actions.getInstitutionLoginsSuccess(transformers.transformQcsInstitutionLoginsToInstitutionLogins(resources), {
      actions.getInstitutionLoginsSuccess(
        mkInstitutionLoginsPayload({
          resources: transformers.transformQcsInstitutionLoginsToInstitutionLogins(resources),
        }),
        {
          context: 'BUMP_CHANGE_DUMP',
        },
      ),
    );
  }
}

export function* institutionLoginsNudgeUpdater(): SagaIterator {
  const process: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpInstitutionLogins');
  if (process) {
    yield put(actions.getInstitutionLogins(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

// =============================================================================
// Refresh Institution Logins
// =============================================================================

export function* preRefreshAccountsCheck(loginsToRefresh: InstitutionLoginProps[], action: any) {
  // log.debug('Starting RefreshAccounts PreCheck');

  const credentialBlobsForRefreshingLogins: InstitutionLoginStore['credentialBlobsForRefreshingLogins'] = yield select(
    getCredentialBlobsForRefreshingLogins,
  );
  const credentialsForRefreshingLogins: InstitutionLoginStore['credentialsForRefreshingLogins'] = yield select(
    getCredentialsForRefreshingLogins,
  );

  for (let i = 0; i < loginsToRefresh.length; ++i) {
    const login = loginsToRefresh[i];
    if (login.ewcLive && login.id) {
      log.debug('Found EWCLive account');
      if (credentialBlobsForRefreshingLogins.get(login.id) || credentialsForRefreshingLogins.get(login.id)) {
        // already have credentials for this login so continue on to checking other logins
        // log.debug('Already have credentials, continuing to next login');
        continue;
      }
      const userActionCancelled: boolean = yield select(wasUserActionCancelled, login.id);
      if (userActionCancelled) {
        // already asked user for credentials, but they cancelled so we won't ask again
        // log.debug('User cancelled credentials, continuing to next login');
        continue;
      }

      // ask user for credentials, then trigger refresh again
      const dialogData = rootUiTypes.mkRootUiData({
        id: uuid(),
        type: 'DIALOG_TYPE_ACCOUNTS_DISCOVERY',
        allowNesting: false,
        props: ImMap({
          mode: 'PROVIDE-CREDENTIALS',
          institutionLogin: login,

          afterCloseAction: action,
        }),
      });

      log.log('Triggering Credential Request', dialogData);
      yield put(rootUiActions.createDialog(dialogData));
      yield put(actions.refreshAccountsTriggeredCredentialsRequest(login.id));

      // terminate refresh (will trigger again after handling credentials for institution-login)
      return false;
    }
  }

  return true;
}

function* triggerGetAccountsAction() {
  // we now deal with deleted institution id's directly in the accounts reducer
  // removing them from REDUX, so no need to call QCS at this point
  // yield put(getAccounts());
}

// =================================================================================================
// Login Actions
// =================================================================================================

// a saga for utilizing the institution-logins/{id}/action endpoints
//
// {
//   id: accountId to impact
//   action: 'close' || 'disconnect' || 'make-manual'
// }
//

// =============================================================================
// Watchers
// =============================================================================

const mkQcsSyncInstitutionLoginsConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<InstitutionLoginProps>();

const mkResourceConfig = (
  successAction: resourceStoreTypes.ResourceActionCreator<InstitutionLoginProps>,
  failureAction: resourceStoreTypes.ResourceFailureActionCreator,
) =>
  mkQcsSyncInstitutionLoginsConfig({
    resourceName: 'institution-login',
    resourceBaseUrl: '/institution-logins',
    getLastSyncDate,

    // 2 required transformers
    transformServerResourceToClientResource: mkInstitutionLogin,
    transformClientResourceToRequestData: transformers.transformInstitutionLoginToRequestData,

    transformResourceWithResponse: transformers.transformLoginResourceWithQCSResponse,
    transformResponseToResources: transformers.transformResponseToInstitutionLogins,
    transformResourceToRequestData: transformers.transformInstitutionLoginToRequestData,

    successAction,
    failureAction,
  });

function* getInstitutionLoginsActionWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action: ReturnType<typeof actions.getInstitutionLogins> = yield take(actions.getInstitutionLogins.type);

    const resourceConfig = mkResourceConfig(actions.getInstitutionLoginsSuccess, actions.getInstitutionLoginsFailure);
    yield call(resourceSagaUtils.qcsSyncGetResources<InstitutionLoginProps>, resourceConfig, action);
  }
}

function* getFiIssuesForLoginsActionWatcher() {
  // This uses the institution API to get latest fi issue on a institution login.
  yield takeLeading(
    actions.getLoginsWithFiIssues.type,
    function* mapFiIssuesToLogins({ meta }: ReturnType<typeof actions.getLoginsWithFiIssues>) {
      try {
        yield put(actions.getInstitutionLogins());

        const { success } = yield race({
          success: take(actions.getInstitutionLoginsSuccess),
          fail: take(actions.getInstitutionLoginsFailure),
        });

        const fiStatusEnabled: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'fiStatus');

        if (success && fiStatusEnabled) {
          const institutionLoginsById: ImMap<string, InstitutionLogin> = yield select(getInstitutionLoginsById);

          const institutionIds: string[] = institutionLoginsById.reduce<string[]>((acc, { institutionId }) => {
            if (institutionId) {
              acc.push(institutionId);
            }
            return acc;
          }, []);

          if (institutionIds.length) {
            const url = `${requireEnvironmentConfig().services_url}/institutions/fi-issues?institutionIds=${institutionIds.join(
              ',',
            )}`;
            try {
              const { data }: { data?: Array<Institution> } = yield call(httpClientInterface.send, 'GET', url);

              // withMutations to determine if any logins actually need updating in the store
              const loginsByIdToUpdate = ImMap<string, InstitutionLogin>().withMutations((mutable) => {
                institutionLoginsById.forEach((login) => {
                  if (login.institutionId && login.channel) {
                    const institutionWithFiIssue = data?.find((institution) => institution.id === login.institutionId);
                    const fiIssue = institutionWithFiIssue?.fiIssue?.channelIssues?.[login.channel as FiChannel]?.[0];
                    if (fiIssue) {
                      const newFiIssue = mkFiIssue(fiIssue);
                      if (!newFiIssue.equals(login.fiIssue)) {
                        mutable.set(login.id, login.set('fiIssue', newFiIssue));
                      }
                    } else if (login.fiIssue) {
                      mutable.set(login.id, login.set('fiIssue', null));
                    }
                  }
                });
                return mutable;
              });
              // Sync only if there's something to update
              if (loginsByIdToUpdate.size > 0) {
                yield put(actions.getLoginsWithFiIssuesSuccess(loginsByIdToUpdate));
              }
            } catch (error: any) {
              yield put(actions.getInstitutionLoginsFailure(error));
            }
          }
        }
      } finally {
        // Resolves even after a failure: a rejection would abort the mobile refresh pass over data the rest
        // of the pass doesn't need.
        meta?.resolve?.(undefined);
      }
    },
  );
}

export function* getInstitutionLoginSuccessActionWatcher() {
  yield takeEvery(
    actions.getInstitutionLoginsSuccess.type,
    function* doInstitutionLoginAction(action: ReturnType<typeof actions.getInstitutionLoginsSuccess>) {
      const institutions: ImMap<string, Institution> = yield select(institutionsSelectors.getInstitutionsById);
      const fetchInstitutionIds = action.payload.resources.reduce<Set<{ id: string; channelType?: string | null }>>(
        (institutionIds, login) => {
          if (login.institutionId && !institutions.has(login.institutionId)) {
            const channelType = login?.channel;
            institutionIds.add({ id: login.institutionId, channelType });
          }
          return institutionIds;
        },
        new Set(),
      );
      const iterator = fetchInstitutionIds[Symbol.iterator]();
      for (let result = iterator.next(); !result.done; result = iterator.next()) {
        const { id, channelType } = result.value;
        yield put(institutionsActions.getInstitution({ id }, { props: { channelType } }));
      }
    },
  );
}

export function* updateInstitutionLoginActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.updateInstitutionLoginSuccess, actions.updateInstitutionLoginFailure);
  yield takeEvery(
    actions.updateInstitutionLogin.type,
    resourceSagaUtils.qcsSyncUpdateResource<InstitutionLoginProps>,
    resourceConfig,
  );
}

export function* deleteInstitutionLoginActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.deleteInstitutionLoginSuccess, actions.deleteInstitutionLoginFailure);
  yield takeEvery(
    actions.deleteInstitutionLogin.type,
    resourceSagaUtils.qcsSyncDeleteResource<InstitutionLoginProps>,
    resourceConfig,
  );
}

export function* deleteInstitutionLoginSuccessActionWatcher() {
  yield takeEvery(actions.deleteInstitutionLoginSuccess.type, triggerGetAccountsAction);
}

export function* qcsInstitutionLoginActionActionWatcher() {
  yield takeLatest(
    actions.qcsInstitutionLoginAction.type,
    function* doInstitutionLoginAction({ payload, meta }: ReturnType<typeof actions.qcsInstitutionLoginAction>) {
      const { qcsAction, id, params } = payload;
      const { resolve, reject } = meta;

      const requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/${id}/${qcsAction.toLowerCase()}`;
      const requestBody = transformers.transformActionParamsToRequestData(qcsAction, params);

      try {
        const { data } = yield call(httpClientInterface.send, 'PUT', requestUrl, requestBody);

        const login = transformers.transformQcsInstitutionLoginToInstitutionLogin(data.resource);
        yield put(actions.qcsInstitutionLoginActionResponse(login));

        // TODO: This could be done in a generalized utility method
        const { impactedResources } = data;
        if (impactedResources?.accounts) {
          const accounts = accountsTransformers.transformQcsAccountsToAccounts(impactedResources.accounts);
          yield put(accountsActions.applyAccountsChanges(accounts));
        }

        yield call(resolve);
      } catch (error: any) {
        log.error(`Error performing QCS account action ${qcsAction}`, error);
        yield call(reject, error);
      }
    },
  );
}

// =================================================================================================
// Refresh Accounts
// =================================================================================================

// poll every 3 seconds up to a total of 3 minutes and 30 seconds (seconds are buffer over QCS timeouts)
const REFRESH_POLL_INTERVAL = 3;
const REFRESH_POLL_ATTEMPTS = 210 / REFRESH_POLL_INTERVAL;

// Logins with no refreshSuccess, refreshError or refreshDelay event yet.
type RefreshTrackingData = Map<string, TrackingProperties>;

type RefreshPass = {
  institutionLoginId: string | null | undefined;
  meta: Meta | undefined;
  startedAt: number;
  loginsTotal: number;
  loginsToRefreshTrackingData: RefreshTrackingData;
};

type RefreshPassEnd = {
  status: Extract<
    InstitutionLoginStatus,
    | 'DONE_PROCESSING_WITH_UPDATE'
    | 'DONE_PROCESSING_WITHOUT_UPDATE'
    | 'FAILED_WHEN_POLLING'
    | 'TIMEOUT_WHILE_PROCESSING'
  >;
  polls: number;
  error?: unknown;
};

// QCS didn't process these refreshes, likely because the logins were refreshed in the past 4 hours.
function trackPostponedRefreshes(
  loginsToRefreshTrackingData: RefreshTrackingData,
  polledIds?: ReadonlySet<string | undefined>,
) {
  loginsToRefreshTrackingData.forEach((trackingData, id) => {
    if (!polledIds?.has(id)) {
      tracker.track(tracker.events.refreshDelay, trackingData);
      loginsToRefreshTrackingData.delete(id);
    }
  });
}

// Reports before refreshAccountsCompleted, which clears the pending user actions.
function* endRefreshPass(pass: RefreshPass, { status, polls, error }: RefreshPassEnd) {
  const userActionPending: boolean = yield select(isUserActionPending);
  const properties = {
    status,
    polls,
    duration_ms: Date.now() - pass.startedAt,
    logins_total: pass.loginsTotal,
    logins_unresolved: pass.loginsToRefreshTrackingData.size,
    user_action_pending: userActionPending,
    http_status: getHttpStatus(error) ?? null,
    qcs_code: getQcsErrorCode(error) ?? null,
  };
  tracker.track(tracker.events.refreshEnd, properties);
  crashReporterInterface.addBreadcrumb('refresh.end', properties, 'state');

  yield put(actions.refreshAccountsCompleted({ institutionLoginId: pass.institutionLoginId, status }, pass.meta));
}

function* refreshPoll(
  url: string,
  loginsToRefreshTrackingData: RefreshTrackingData,
): Generator<unknown, RefreshPassEnd> {
  assert(url, 'polling url is invalid - that should never happen...');
  for (let polls = 1; polls <= REFRESH_POLL_ATTEMPTS; polls += 1) {
    try {
      const response: { data: transformers.QCSRefreshResponse } = yield call(httpClientInterface.send, 'GET', url);
      log.log('Polling for refresh status', response.data);

      const refreshStatus = transformers.transformResponseToRefreshStatus(response.data);
      log.log('Status after transforming...', refreshStatus);

      // send refresh status
      yield put(actions.refreshAccountsStatuses(refreshStatus));

      let userActionIsPending: boolean = yield select(isUserActionPending);
      const { institutionLogins } = refreshStatus;
      for (let i = 0; i < institutionLogins.size; ++i) {
        const login = institutionLogins.get(i);
        if (login?.status === 'ACTION_REQUIRED' && login.id) {
          const userActionAlreadyQueued: boolean = yield select(hasUserActionBeenQueued, login.id);
          log.log('Refresh login status has action required', login, userActionAlreadyQueued);
          if (!userActionAlreadyQueued && login.aggregators && login.aggregators.size > 0) {
            // todo: currently QCS only supports a single aggregator per login, will need to fix when QCS supports multiple aggregators per login
            const aggregator = login.aggregators.get(0);

            // todo: this should be checking aggregator.aggStatus === 'MFA_CHALLENGE_READY_FOR_USER' but QCS isn't setting this
            if (aggregator && aggregator.mfaChallenges) {
              const mfaChallenge = transformers.transformMfaResponseToMfaChallenge(
                login.id,
                aggregator.mfaChallenges as transformers.QCSChallengeItem[],
                login.submitUrl ? login.submitUrl : '',
              );

              const institutionLogin: InstitutionLoginProps = yield select(
                getInstitutionLoginForId,
                mfaChallenge.institutionLoginId,
              );

              // If a re-auth flow is already active/queued for this login, do not nest MFA on top of it.
              // This ensures "Re-authorization required" is shown before MFA, regardless of action timing.
              const rootUiStore: ReturnType<typeof rootUiSelectors.getRootUiStore> = yield select(
                rootUiSelectors.getRootUiStore,
              );
              const allModals = rootUiStore.activeModals.concat(rootUiStore.queuedModals);
              const hasReauthDialogForLogin = allModals.some((modal) => {
                if (modal.type !== 'DIALOG_TYPE_ACCOUNTS_DISCOVERY') {
                  return false;
                }
                const mode = modal.props.get('mode');
                if (mode !== 'RE-AUTHENTICATE') {
                  return false;
                }
                const modalLogin = modal.props.get('institutionLogin') as InstitutionLogin | undefined;
                return modalLogin?.id === mfaChallenge.institutionLoginId;
              });

              const mfaChallengeDialogData = rootUiTypes.mkRootUiData({
                id: uuid(),
                type: 'DIALOG_TYPE_ACCOUNTS_DISCOVERY',
                allowNesting: !userActionIsPending && !hasReauthDialogForLogin, // ok to nest unless another user action (incl. reauth) is pending
                props: ImMap({
                  mode: 'PROVIDE-MFA',
                  institutionLogin,
                  mfaChallenge,
                }),
              });

              log.log('Triggering MFA', mfaChallenge);
              yield put(rootUiActions.createDialog(mfaChallengeDialogData));
              yield put(actions.refreshAccountsTriggeredMfa(mfaChallenge.institutionLoginId));
              userActionIsPending = true;
            }
          }
        } else if (login?.id && !login.isProcessing && loginsToRefreshTrackingData.has(login.id)) {
          const data = loginsToRefreshTrackingData.get(login.id);
          loginsToRefreshTrackingData.delete(login.id);
          const aggregator = login.aggregators?.first?.();
          const trackingData = {
            ...data,
            error_code: aggregator?.aggStatus,
            agg_status: aggregator?.aggStatus,
            agg_status_code: aggregator?.cpAggStatusCode,
            agg_status_detail: aggregator?.cpAggStatusDetail,
          };
          if (login.status === 'OK') {
            tracker.track(tracker.events.refreshSuccess, trackingData);
          } else {
            tracker.track(tracker.events.refreshError, trackingData);
          }
        }
      }

      if (!userActionIsPending && !response.data.isProcessing) {
        // QCS leaves postponed logins out of the job, so one it still lists (ACTION_REQUIRED) stays unresolved.
        trackPostponedRefreshes(loginsToRefreshTrackingData, new Set(institutionLogins.map((login) => login.id)));

        // server is done processing and we are not waiting for an MFA so stop polling
        // todo: can maybe optimize by not polling while waiting for MFA but need to restart once MFA is submitted
        return { status: 'DONE_PROCESSING_WITH_UPDATE', polls };
      }

      log.log(`Number of times left to poll is ${REFRESH_POLL_ATTEMPTS - polls} #refresh`);
      yield delay(REFRESH_POLL_INTERVAL * 1000);
    } catch (error) {
      log.log('How did we get here...', error);
      return { status: 'FAILED_WHEN_POLLING', polls, error };
    }
  }

  const idsRefreshingWithUserActionsPending: InstitutionLoginStore['idsRefreshingWithUserActionsPending'] =
    yield select(getIdsRefreshingWithUserActionsPending);
  if (idsRefreshingWithUserActionsPending.size > 0) {
    yield put(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          type: 'DIALOG_REFRESH_TIMEOUT',
          props: ImMap({
            dialogIdsToClose: idsRefreshingWithUserActionsPending.toList(),
            confirmationDialogProps: Record({
              content: 'The server terminated the refresh operation due to length of time.',
              confirmLabel: 'retry',
              denyLabel: 'cancel',
              confirmAction: actions.refreshAccounts(),
            })(),
          }),
        }),
      ),
    );
  }
  return { status: 'TIMEOUT_WHILE_PROCESSING', polls: REFRESH_POLL_ATTEMPTS };
}

export function* refreshAccounts(action: ReturnType<typeof actions.refreshAccounts>) {
  const { payload: data, meta } = action;
  const institutionLoginId = data != null ? data.institutionLoginId : null;

  const loginsToRefresh: InstitutionLoginProps[] = [];
  if (institutionLoginId) {
    const login: InstitutionLoginProps = yield select(getInstitutionLoginForId, institutionLoginId);
    if (login?.id) {
      const refreshCancelledForLogin: boolean = yield select(wasUserActionCancelled, login.id);
      if (!refreshCancelledForLogin) {
        loginsToRefresh.push(login);
      }
    }
  } else {
    const loginsById: ImMap<string, InstitutionLoginProps> = yield select(getActiveConnectedInstitutionLogins);

    const allLogins = loginsById.valueSeq().toArray();
    for (let i = 0; i < allLogins.length; ++i) {
      const login = allLogins[i];
      if (login?.id) {
        const refreshCancelledForLogin: boolean = yield select(wasUserActionCancelled, login.id);
        if (!refreshCancelledForLogin) {
          loginsToRefresh.push(login);
        }
      }
    }
  }

  if (loginsToRefresh.length === 0) {
    yield put(actions.refreshAccountsCompleted({ institutionLoginId, status: 'NO_ACCOUNTS_TO_REFRESH' }, meta));
    return;
  }

  const precheckPassed = yield* preRefreshAccountsCheck(loginsToRefresh, action);
  if (!precheckPassed) {
    yield put(actions.refreshAccountsPrecheck());
    return;
  }

  const loginsToRefreshTrackingData: RefreshTrackingData = new Map();
  for (let i = 0; i < loginsToRefresh.length; i++) {
    const login = loginsToRefresh[i];
    if (login.id) {
      const aggregator = login.aggregators?.first?.();
      const aggregatorName: string = yield select(getTrackingAggregator, login.id);
      const institution: Institution = login.institutionId
        ? yield select(institutionsSelectors.getInstitution, login.institutionId)
        : null;
      const trackingData = {
        aggregator: aggregatorName,
        institution_login_id: login.id,
        institution_id: login.institutionId,
        institution_name: login.channel === 'ZILLOW' ? 'Zillow' : (institution?.name ?? 'unknown'),
        institution_login_channel: login.channel,
        channel: institution?.fdp?.channelData?.type,
        institution_login_optional_action: login.optionalAction,
        error_code: aggregator?.aggStatus,
        agg_status: aggregator?.aggStatus,
        agg_status_code: aggregator?.aggStatusCode,
        agg_status_detail: aggregator?.aggStatusDetail,
        refreshFrequency: login.refreshFrequency,
      };
      loginsToRefreshTrackingData.set(login.id, trackingData);

      tracker.track(tracker.events.refreshStart, trackingData);
    }
  }
  const pass: RefreshPass = {
    institutionLoginId,
    meta,
    startedAt: Date.now(),
    loginsTotal: loginsToRefreshTrackingData.size,
    loginsToRefreshTrackingData,
  };

  let requestUrl;
  if (institutionLoginId) {
    requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/${institutionLoginId}/refresh`;
  } else {
    requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/refresh`;
  }

  const credentialsBlobsForRefreshingLogins: InstitutionLoginStore['credentialBlobsForRefreshingLogins'] = yield select(
    getCredentialBlobsForRefreshingLogins,
  );
  const credentialsForRefreshingLogins: InstitutionLoginStore['credentialsForRefreshingLogins'] = yield select(
    getCredentialsForRefreshingLogins,
  );
  const requestBody = transformers.transformRefreshingLoginsToRequestData(
    loginsToRefresh,
    credentialsBlobsForRefreshingLogins,
    credentialsForRefreshingLogins,
  );

  let pollingReference = '';

  try {
    const response: { data: { pollingReference: string; status?: string } } = yield call(
      httpClientInterface.send,
      'POST',
      requestUrl,
      requestBody,
    );
    log.log('Refresh accounts response is', response.data);

    pollingReference = response.data.pollingReference;
    log.log('pollingReference=', pollingReference);
  } catch (error) {
    log.log('Error refreshing accounts', error);
    yield* endRefreshPass(pass, { status: 'FAILED_WHEN_POLLING', polls: 0, error });

    // if we failed to kickoff refresh, were done
    return;
  }

  // poll until refresh is ready or cancel action
  if (pollingReference && pollingReference.length && pollingReference.length > 0) {
    requestUrl = `${requireEnvironmentConfig().services_url}${pollingReference}`;
    const passEnd = yield* refreshPoll(requestUrl, loginsToRefreshTrackingData);
    yield* endRefreshPass(pass, passEnd);
  } else {
    trackPostponedRefreshes(loginsToRefreshTrackingData);
    yield* endRefreshPass(pass, { status: 'DONE_PROCESSING_WITHOUT_UPDATE', polls: 0 });
  }
}

export function* refreshAccountsCompletedWatcher() {
  yield takeEvery(actions.refreshAccountsCompleted, function* refreshAccountsSuccess({ payload, meta }) {
    if (meta?.resolve) {
      yield call(meta?.resolve, payload);
    }
  });
}

export function* refreshAccountsWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action: ReturnType<typeof actions.refreshAccounts> = yield take(actions.refreshAccounts.type);
    yield call(refreshAccounts, action);
  }
}

export function* submitMfaResponse(action: ReturnType<typeof actions.refreshAccountsMfaFormSubmit>) {
  const {
    payload: { mfaResponse },
    meta,
  } = action;
  const { resolve, reject } = meta;

  // TODO: If user submits an MFA response but we are no longer polling, perhaps we should just fail...

  const requestUrl = `${requireEnvironmentConfig().services_url}${mfaResponse.submitUrl}`;
  const requestBody = {
    institutionLoginId: mfaResponse.institutionLoginId,
    mfaResponses: mfaResponse.answers.map((answer) => ({
      key: answer.id,
      response: answer.value,
    })),
  };

  try {
    yield call(httpClientInterface.send, 'POST', requestUrl, requestBody);

    yield put(
      actions.refreshAccountsMfaFormSubmitResponse(
        { institutionLoginId: mfaResponse.institutionLoginId },
        actionHelpers.cpMetaForResponseAction(meta) ?? undefined,
      ),
    );
    yield call(resolve, mfaResponse.institutionLoginId);
  } catch (error: any) {
    log.log('Received error while submitting MFA', mfaResponse.institutionLoginId, error);
    const errorResponse = new RefreshAccountsMfaFormSubmitError(error, mfaResponse.institutionLoginId);

    yield put(
      actions.refreshAccountsMfaFormSubmitResponse(
        errorResponse,
        actionHelpers.cpMetaForResponseAction(meta) ?? undefined,
      ),
    );
    yield call(reject, errorResponse);
  }
}

export function* refreshAccountsMfaFormSubmitActionWatcher() {
  yield takeEvery(actions.refreshAccountsMfaFormSubmit.type, submitMfaResponse);
}

export function* batchInstitutionLoginsActionWatcher() {
  yield takeEvery(
    actions.batchInstitutionLogins.type,
    resourceSagaUtils.qcsSyncUpdateResources<InstitutionLoginProps>,
    mkResourceConfig(actions.batchInstitutionLoginsSuccess, actions.batchInstitutionLoginsFailure),
  );
}

export function* updateRefreshStatusActionWater() {
  yield takeEvery(actions.updateRefreshStatus.type, function* (action: ReturnType<typeof actions.updateRefreshStatus>) {
    const { payload: institutionLogin, meta } = action;
    const { resolve, reject } = meta;
    const requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins`;
    const requestBody = {
      id: institutionLogin.id,
      clientId: institutionLogin.clientId,
      name: institutionLogin.name,
      aggregators: [
        {
          cpId: institutionLogin.aggregators?.get(0)?.cpId,
          cpChannel: institutionLogin.aggregators?.get(0)?.channel,
          aggStatus: institutionLogin.aggregators?.get(0)?.aggStatus,
        },
      ],
      channel: institutionLogin.channel,
      cpSetupMode: 'UPDATE_STATUS',
    };

    try {
      yield call(httpClientInterface.send, 'POST', requestUrl, requestBody);

      yield call(resolve, institutionLogin.id);
    } catch (error: any) {
      log.log('Received error updating refresh status', institutionLogin.id, error);
      yield call(reject, error);
    }
  });
}

export default [
  getInstitutionLoginsActionWatcher as Saga,
  getFiIssuesForLoginsActionWatcher,
  updateInstitutionLoginActionWatcher,
  deleteInstitutionLoginActionWatcher,
  deleteInstitutionLoginSuccessActionWatcher,
  getInstitutionLoginSuccessActionWatcher as Saga,
  qcsInstitutionLoginActionActionWatcher,
  refreshAccountsWatcher as Saga,
  refreshAccountsMfaFormSubmitActionWatcher,
  refreshAccountsCompletedWatcher,
  batchInstitutionLoginsActionWatcher,
  updateRefreshStatusActionWater,
] satisfies Saga[];
